import type { Order } from '@/types/commerce'
import { LIVE_SUBSCRIPTION_STATUSES } from './commerce'

/** Matches any plan the user still holds, when used in `inclusive` or `exclusive`. */
export const ANY_PLAN = '*'

/**
 * Standard plan-visibility rule, shared by configurable modals (`plan-check`)
 * and banners (`IBanner.plans`). Ids are product `_id`s from the cinematic;
 * `'*'` matches any plan still held — including one whose payment failed, so a
 * past-due subscriber is never treated as a non-subscriber. Answers WHICH
 * PRODUCT, not whether billing is healthy: that is `IOrderStatusRule`, and the
 * two compose with AND. An absent/empty rule matches everyone.
 */
export interface IPlanRule {
  inclusive?: string[]
  exclusive?: string[]
}

const activeProductIds = (orders: Order[]): Set<string> => {
  const ids = new Set<string>()
  orders.forEach((order) => {
    if (LIVE_SUBSCRIPTION_STATUSES.includes(order?.status)) {
      const productId = order?.product?._id
      if (productId) ids.add(productId)
    }
  })
  return ids
}

const listMatches = (list: string[], active: Set<string>): boolean =>
  list.some((id) => (id === ANY_PLAN ? active.size > 0 : active.has(id)))

/**
 * True when the plans the user still holds satisfy the rule: none in
 * `exclusive`, and — when `inclusive` is non-empty — at least one in
 * `inclusive`. Empty/absent rule → true (visible to everyone, subscribers or
 * not).
 */
export const hasPlans = (orders: Order[] = [], plans: IPlanRule = {}): boolean => {
  const inclusive = (plans.inclusive ?? []).filter(Boolean)
  const exclusive = (plans.exclusive ?? []).filter(Boolean)

  if (!inclusive.length && !exclusive.length) return true

  const active = activeProductIds(orders)

  if (listMatches(exclusive, active)) return false
  if (inclusive.length && !listMatches(inclusive, active)) return false

  return true
}
